/**
 * Generic Accordion Toggle
 *
 * Reusable for agenda, speakers, FAQs, etc.
 */

export interface AccordionOptions {
  selector: string;
  onToggle?: (toggle: HTMLButtonElement, isExpanded: boolean) => void;
}

const ANIM_KEY = '_accordionAnim';

function clearAnim(target: HTMLElement): void {
  const id = (target as Record<string, unknown>)[ANIM_KEY] as number | undefined;
  if (id) cancelAnimationFrame(id);
  target.style.removeProperty('height');
  target.style.removeProperty('overflow');
  target.style.removeProperty('transition');
  target.style.removeProperty('opacity');
}

export function initAccordion(options: AccordionOptions): void {
  const { selector, onToggle } = options;
  const toggles = document.querySelectorAll<HTMLButtonElement>(selector);

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const targetId = toggle.getAttribute('aria-controls');
      if (!targetId) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;

      toggle.setAttribute('aria-expanded', String(newState));
      toggle.classList.toggle('is-expanded', newState);

      // Cancel any in-flight animation
      clearAnim(target);

      if (newState) {
        target.hidden = false;
        target.style.overflow = 'hidden';
        target.style.height = '0';
        target.style.opacity = '0';
        const h = target.scrollHeight + 'px';
        target.style.transition = 'height 0.3s ease, opacity 0.3s ease';
        (target as Record<string, unknown>)[ANIM_KEY] = requestAnimationFrame(() => {
          target.style.height = h;
          target.style.opacity = '1';
        });
        target.addEventListener('transitionend', () => clearAnim(target), { once: true });
      } else {
        target.style.overflow = 'hidden';
        target.style.height = target.scrollHeight + 'px';
        target.style.opacity = '1';
        target.style.transition = 'none';
        (target as Record<string, unknown>)[ANIM_KEY] = requestAnimationFrame(() => {
          target.style.transition = 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease';
          target.style.height = '0';
          target.style.opacity = '0';
        });
        target.addEventListener(
          'transitionend',
          (e) => {
            if (e.propertyName === 'height') {
              target.hidden = true;
              clearAnim(target);
            }
          },
          { once: true },
        );
      }

      onToggle?.(toggle, newState);
    });
  });
}
